import { useMemo, useState, useEffect, useRef } from "react";
import {
    type ColumnDef,
    type RowData,
    SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/shadcn/table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Loader } from "../../common/loader";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/shadcn/tooltip";

declare module "@tanstack/react-table" {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends RowData, TValue> {
        shortHeader?: string;
        label?: string;
        description?: string;
    }
}

interface CompactStatsTableProps<TData> {
    columns: ColumnDef<TData>[];
    data: TData[];
    isLoading?: boolean;
    defaultSortId?: string;
}

export function CompactStatsTable<TData extends Record<string, unknown>>({
    columns,
    data,
    isLoading = false,
    defaultSortId = "totalPoints",
}: CompactStatsTableProps<TData>) {
    const [sorting, setSorting] = useState<SortingState>([{ id: defaultSortId, desc: true }]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(Infinity);

    useEffect(() => {
        setSorting([{ id: defaultSortId, desc: true }]);
    }, [data, defaultSortId]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setContainerWidth(entry.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const isNarrow = containerWidth < 790;

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: { sorting },
    });

    const columnRankings = useMemo(() => {
        const rankings: Record<
            string,
            { top3: number[]; highest: number | null; second: number | null; third: number | null }
        > = {};

        const currentRows = table.getRowModel().rows;

        table.getAllColumns().forEach((column) => {
            const columnValues = currentRows
                .map((r) => {
                    const cell = r.getAllCells().find((c) => c.column.id === column.id);
                    return cell?.getValue();
                })
                .filter((value): value is number => typeof value === "number")
                .sort((a, b) => b - a);

            const uniqueValues = [...new Set(columnValues)];
            rankings[column.id] = {
                top3: uniqueValues.slice(0, 3),
                highest: uniqueValues.length > 0 ? uniqueValues[0] : null,
                second: uniqueValues.length > 1 ? uniqueValues[1] : null,
                third: uniqueValues.length > 2 ? uniqueValues[2] : null,
            };
        });

        return rankings;
    }, [table]);

    if (isLoading) {
        return (
            <div
                className="w-full flex items-center justify-center bg-surface-container-lowest"
                style={{ minHeight: "200px" }}
            >
                <Loader message="Loading..." />
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div
                className="w-full flex items-center justify-center bg-surface-container-lowest"
                style={{ minHeight: "80px" }}
            >
                <span className="text-center p-3 text-xs text-on-surface-variant">No data available</span>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="w-full bg-surface-container-lowest overflow-hidden">
            <Table className="w-full border-collapse table-auto">
                <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id} className="border-0">
                            {hg.headers.map((header) => {
                                const isSorted = header.column.getIsSorted();
                                const meta = header.column.columnDef.meta;
                                const useShortHeader = isNarrow && meta?.shortHeader !== undefined;
                                return (
                                    <TableHead
                                        key={header.id}
                                        onClick={
                                            header.column.getCanSort()
                                                ? () =>
                                                      header.column.toggleSorting(header.column.getIsSorted() === "asc")
                                                : undefined
                                        }
                                        className={`
                                            px-2 py-1.5 font-headline font-black text-[9px] uppercase tracking-wider
                                            select-none transition-colors whitespace-nowrap overflow-hidden
                                            ${header.column.getCanSort() ? "cursor-pointer" : ""}
                                            ${isSorted ? "data-table-header-sorted" : ""}
                                            data-table-header
                                        `}
                                    >
                                        {!header.isPlaceholder && (
                                            <div className="flex gap-1 items-center">
                                                {useShortHeader ? (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span className="cursor-help">{meta!.shortHeader}</span>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>{meta!.description ?? meta!.label}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                ) : (
                                                    flexRender(header.column.columnDef.header, header.getContext())
                                                )}
                                                {header.column.getCanSort() && (
                                                    <span className="shrink-0">
                                                        {isSorted === "asc" ? (
                                                            <ArrowUp className="h-2.5 w-2.5" />
                                                        ) : isSorted === "desc" ? (
                                                            <ArrowDown className="h-2.5 w-2.5" />
                                                        ) : (
                                                            <ArrowUpDown className="h-2.5 w-2.5" />
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id} className="data-table-row transition-colors border-0">
                            {row.getVisibleCells().map((cell) => {
                                const value = cell.getValue();
                                const columnId = cell.column.id;

                                let highlightColor: string | undefined;
                                if (
                                    typeof value === "number" &&
                                    columnRankings[columnId] &&
                                    columnId !== "rank" &&
                                    columnId !== "totalPoints"
                                ) {
                                    const { highest, second, third } = columnRankings[columnId];
                                    if (value !== 0) {
                                        if (value === highest) highlightColor = "var(--color-medal-gold)";
                                        else if (value === second) highlightColor = "var(--color-rank-silver)";
                                        else if (value === third) highlightColor = "var(--color-rank-bronze)";
                                    }
                                }

                                return (
                                    <TableCell
                                        key={cell.id}
                                        className="px-2 py-1 text-xs text-on-surface whitespace-nowrap overflow-hidden text-ellipsis"
                                        style={{
                                            color: highlightColor,
                                            fontWeight: highlightColor ? 700 : undefined,
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
